import React, { useState, useEffect, useMemo } from 'react';
import axiosInstance from '../../services/api/axios';
import { Loader2, FileText, Expand, X, ChevronDown, ChevronRight } from 'lucide-react';

const PREVIEW_AREA_LIMIT = 1; // show one unit area in the inline preview, "View all" opens modal

// Map a single material item from the grouped response into the read-only row shape (v3_58:
// free-text material + quantity + unit of measure; no category chain, no brand/specs/price).
function mapItemToReadRow(item) {
    const qty = parseFloat(item.itemQuantity) || 0;
    return {
        materialName: item.materialName ?? '',
        quantity: qty,
        unit: item.itemUnit ?? '',
        notes: item.notes ?? '',
    };
}

const MaterialRowsTable = ({ rows }) => (
    <table className="w-full text-sm text-left border-collapse">
        <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
                <th className="py-2 px-3 font-medium text-slate-600">Material</th>
                <th className="py-2 px-3 font-medium text-slate-600 text-right">Qty</th>
                <th className="py-2 px-3 font-medium text-slate-600">Unit</th>
                <th className="py-2 px-3 font-medium text-slate-600">Notes</th>
            </tr>
        </thead>
        <tbody>
            {rows.map((row, idx) => (
                <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="py-2 px-3 text-slate-900">{row.materialName || '—'}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{row.quantity}</td>
                    <td className="py-2 px-3 text-slate-700">{row.unit || '—'}</td>
                    <td className="py-2 px-3 text-slate-600 max-w-[160px] truncate" title={row.notes}>
                        {row.notes}
                    </td>
                </tr>
            ))}
        </tbody>
    </table>
);

const BoqTree = ({ areas }) => {
    const [collapsed, setCollapsed] = useState(() => new Set());
    const toggle = (key) => {
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    return (
        <div className="flex flex-col gap-3">
            {areas.map((area, aIdx) => {
                const areaKey = `a-${aIdx}`;
                const isAreaCollapsed = collapsed.has(areaKey);
                const totalRows = area.workItems.reduce((acc, wi) => acc + wi.rows.length, 0);
                return (
                    <div
                        key={areaKey}
                        className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"
                    >
                        <button
                            type="button"
                            onClick={() => toggle(areaKey)}
                            className="w-full flex items-center gap-2 px-4 py-3 bg-slate-50/70 border-b border-slate-100 hover:bg-slate-100/70 transition-colors"
                        >
                            {isAreaCollapsed ? (
                                <ChevronRight size={18} className="text-slate-500" />
                            ) : (
                                <ChevronDown size={18} className="text-slate-500" />
                            )}
                            <span className="font-semibold text-slate-800">
                                {area.areaName || 'Unnamed area'}
                            </span>
                            <span className="text-xs text-slate-500 px-2 py-0.5 bg-white rounded-full border border-slate-200">
                                {area.workItems.length} {area.workItems.length === 1 ? 'work item' : 'work items'} · {totalRows} {totalRows === 1 ? 'row' : 'rows'}
                            </span>
                            <div className="flex-1" />
                        </button>
                        {!isAreaCollapsed && (
                            <div className="p-3 flex flex-col gap-2 bg-white">
                                {area.workItems.map((wi, wIdx) => {
                                    const wiKey = `${areaKey}-w-${wIdx}`;
                                    const isWiCollapsed = collapsed.has(wiKey);
                                    return (
                                        <div
                                            key={wiKey}
                                            className="rounded-xl border border-slate-100 bg-slate-50/40 overflow-hidden"
                                        >
                                            <button
                                                type="button"
                                                onClick={() => toggle(wiKey)}
                                                className="w-full flex items-center gap-2 px-3 py-2 bg-white/60 border-b border-slate-100 hover:bg-white transition-colors"
                                            >
                                                {isWiCollapsed ? (
                                                    <ChevronRight size={16} className="text-slate-500" />
                                                ) : (
                                                    <ChevronDown size={16} className="text-slate-500" />
                                                )}
                                                <span className="text-sm font-medium text-slate-800">
                                                    {wi.workItemName || wi.executionPackageLabel || 'Unnamed work item'}
                                                </span>
                                                {wi.executionPackageLabel && (
                                                    <span className="text-[10px] font-semibold uppercase text-violet-600 px-2 py-0.5 bg-violet-50 rounded-full border border-violet-200">
                                                        {wi.executionPackageLabel}
                                                    </span>
                                                )}
                                                <span className="text-xs text-slate-500 px-2 py-0.5 bg-white rounded-full border border-slate-200">
                                                    {wi.rows.length} {wi.rows.length === 1 ? 'row' : 'rows'}
                                                </span>
                                                <div className="flex-1" />
                                            </button>
                                            {!isWiCollapsed && (
                                                <div className="overflow-x-auto">
                                                    <MaterialRowsTable rows={wi.rows} />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const BoqReadOnlyView = ({ designIterationId, designSubmissionId, portfolioId }) => {
    const [groupedData, setGroupedData] = useState({ unitAreas: [], ungroupedMaterials: [] });
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const fetchBoq = async () => {
            setLoading(true);
            try {
                const fetchGrouped = async (queryKey, queryVal) => {
                    const params = new URLSearchParams();
                    params.append(queryKey, queryVal);
                    const response = await axiosInstance.get(
                        `/interior-designers/id-boq/grouped?${params.toString()}`,
                        { skipAuthLogoutOn401: true }
                    );
                    const raw = response?.data ?? response;
                    return {
                        unitAreas: Array.isArray(raw?.unitAreas) ? raw.unitAreas : [],
                        ungroupedMaterials: Array.isArray(raw?.ungroupedMaterials)
                            ? raw.ungroupedMaterials
                            : [],
                    };
                };

                let iterationIds = [];
                if (designIterationId) {
                    iterationIds = [designIterationId];
                } else if (designSubmissionId) {
                    // `designSubmissionId` is the lead id now — deliverables live on the lead.
                    const deliverablesResponse = await axiosInstance.get(
                        '/interior-designers/design-deliverables',
                        {
                            params: { id_lead_id: parseInt(designSubmissionId) },
                            skipAuthLogoutOn401: true,
                        }
                    );
                    const iterations = Array.isArray(deliverablesResponse)
                        ? deliverablesResponse
                        : Array.isArray(deliverablesResponse?.items)
                          ? deliverablesResponse.items
                          : Array.isArray(deliverablesResponse?.data)
                            ? deliverablesResponse.data
                            : [];
                    const sortedIterations = [...iterations].sort((a, b) => {
                        const numA = a?.iterationNumber || 0;
                        const numB = b?.iterationNumber || 0;
                        if (numA !== numB) return numB - numA;
                        const tA = new Date(a?.createdDate || a?.created_date || 0).getTime();
                        const tB = new Date(b?.createdDate || b?.created_date || 0).getTime();
                        return tB - tA;
                    });
                    iterationIds = sortedIterations
                        .map((it) => it?.designIterationId || it?.design_iteration_id || it?.id)
                        .filter(Boolean);
                } else if (portfolioId) {
                    const result = await fetchGrouped('portfolio_item_id', portfolioId);
                    if (!cancelled) setGroupedData(result);
                    return;
                } else {
                    if (!cancelled) setGroupedData({ unitAreas: [], ungroupedMaterials: [] });
                    return;
                }

                if (iterationIds.length === 0) {
                    if (!cancelled) setGroupedData({ unitAreas: [], ungroupedMaterials: [] });
                    return;
                }

                let resolved = { unitAreas: [], ungroupedMaterials: [] };
                for (const iterationId of iterationIds) {
                    const data = await fetchGrouped('design_iteration_id', iterationId);
                    if (data.unitAreas.length > 0 || data.ungroupedMaterials.length > 0) {
                        resolved = data;
                        break;
                    }
                }
                if (!cancelled) setGroupedData(resolved);
            } catch (err) {
                console.error('BoqReadOnlyView fetch error:', err);
                if (!cancelled) setGroupedData({ unitAreas: [], ungroupedMaterials: [] });
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchBoq();
        return () => {
            cancelled = true;
        };
    }, [designIterationId, designSubmissionId, portfolioId]);

    const areas = useMemo(() => {
        const built = (groupedData.unitAreas || []).map((group) => {
            const ua = group?.unitArea ?? group?.unit_area ?? {};
            const wis = Array.isArray(group?.workItems)
                ? group.workItems
                : Array.isArray(group?.work_items)
                  ? group.work_items
                  : [];
            return {
                areaName: ua.areaName ?? ua.area_name ?? '',
                workItems: wis.map((wig) => {
                    const wi = wig?.workItem ?? wig?.work_item ?? {};
                    const mats = Array.isArray(wig?.materials) ? wig.materials : [];
                    return {
                        workItemName: wi.workItemName ?? wi.work_item_name ?? '',
                        executionPackageLabel:
                            wi.executionPackageLabel ?? wi.execution_package_label ?? null,
                        rows: mats.map(mapItemToReadRow),
                    };
                }),
            };
        });
        const ungrouped = groupedData.ungroupedMaterials || [];
        if (ungrouped.length > 0) {
            built.push({
                areaName: 'Ungrouped',
                workItems: [
                    {
                        workItemName: 'Ungrouped',
                        rows: ungrouped.map(mapItemToReadRow),
                    },
                ],
            });
        }
        return built;
    }, [groupedData]);

    const totalRows = useMemo(
        () =>
            areas.reduce(
                (acc, a) =>
                    acc + a.workItems.reduce((wAcc, wi) => wAcc + wi.rows.length, 0),
                0
            ),
        [areas]
    );

    const hasMore = areas.length > PREVIEW_AREA_LIMIT;
    const previewAreas = hasMore ? areas.slice(0, PREVIEW_AREA_LIMIT) : areas;

    useEffect(() => {
        if (modalOpen) {
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [modalOpen]);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-12 gap-2">
                <Loader2 className="w-8 h-8 text-flamingo-500 animate-spin" strokeWidth={2} />
                <p className="text-sm text-slate-500">Loading BOQ…</p>
            </div>
        );
    }

    if (totalRows === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400 border border-dashed border-slate-200 rounded-lg">
                <FileText className="mb-2" size={24} />
                <p className="text-sm">No BOQ for this iteration</p>
            </div>
        );
    }

    return (
        <>
            {hasMore && (
                <div className="flex items-center justify-end mb-2">
                    <button
                        type="button"
                        onClick={() => setModalOpen(true)}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-flamingo-600 hover:text-flamingo-700 hover:bg-flamingo-50 px-3 py-1.5 rounded-md transition-colors"
                    >
                        <Expand size={14} />
                        View all {areas.length} unit areas ({totalRows} items)
                    </button>
                </div>
            )}
            <div className="rounded-lg">
                <BoqTree areas={previewAreas} />
            </div>

            {modalOpen && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="relative bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
                        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200 shrink-0">
                            <h3 className="text-base font-semibold text-slate-900">
                                Bill of Quantities
                                <span className="ml-2 text-sm font-normal text-slate-500">
                                    ({totalRows} items across {areas.length} unit areas)
                                </span>
                            </h3>
                            <button
                                type="button"
                                onClick={() => setModalOpen(false)}
                                className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-slate-100 transition-colors text-slate-500 hover:text-slate-700"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="flex-1 overflow-auto p-4">
                            <BoqTree areas={areas} />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default BoqReadOnlyView;
